import { ScanResult, ScanSummary, createScanSummary } from './types';

export function runMobileLayoutScan(): ScanSummary {
  console.group('📱 Mobile Layout & Breakpoints Scan');
  
  const results: ScanResult[] = [];

  try {
    // Check 1: Viewport meta tag
    const viewport = document.querySelector('meta[name="viewport"]');
    const viewportContent = viewport?.getAttribute('content') || '';
    
    results.push({
      name: 'Viewport Meta Tag',
      status: viewport && viewportContent.includes('width=device-width') ? 'pass' : 'fail',
      message: viewport 
        ? `Viewport configured: ${viewportContent}` 
        : 'Viewport meta tag missing',
      details: 'Critical for responsive mobile layout'
    });

    // Check 2: Current viewport width
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 640;
    const isTablet = viewportWidth >= 640 && viewportWidth < 1024;
    const isDesktop = viewportWidth >= 1024;

    results.push({
      name: 'Current Viewport',
      status: 'info',
      message: `Viewport width: ${viewportWidth}px`,
      details: isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'
    });

    // Check 3: Horizontal overflow detection
    const bodyWidth = document.body.scrollWidth;
    const hasHorizontalOverflow = bodyWidth > viewportWidth;

    results.push({
      name: 'Horizontal Overflow',
      status: hasHorizontalOverflow ? 'fail' : 'pass',
      message: hasHorizontalOverflow 
        ? `Overflow detected: body width ${bodyWidth}px > viewport ${viewportWidth}px`
        : 'No horizontal overflow detected',
      details: 'Horizontal scrolling should be avoided on mobile'
    });

    // Check 4: Touch targets size
    const buttons = document.querySelectorAll('button, a');
    const smallTouchTargets: number[] = [];

    buttons.forEach((button, index) => {
      // Skip keyboard keys (intentionally compact for layout)
      if (button.closest('[class*="keyboard"]') || button.closest('[data-keyboard-key]')) {
        return;
      }
      
      const rect = button.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && (rect.width < 44 || rect.height < 44)) {
        smallTouchTargets.push(index);
      }
    });

    results.push({
      name: 'Touch Target Size',
      status: smallTouchTargets.length === 0 ? 'pass' : 'info',
      message: smallTouchTargets.length === 0
        ? `All ${buttons.length} interactive elements meet 44px minimum`
        : `${smallTouchTargets.length} element(s) below 44px minimum`,
      details: 'iOS Human Interface Guidelines recommend 44x44pt minimum'
    });

    // Check 5: Font size readability
    const textElements = document.querySelectorAll('p, span, a, button, label');
    let smallTextCount = 0;

    textElements.forEach(element => {
      const fontSize = parseFloat(window.getComputedStyle(element).fontSize);
      if (fontSize < 14 && element.textContent && element.textContent.trim().length > 0) {
        smallTextCount++;
      }
    });

    results.push({
      name: 'Font Size Readability',
      status: smallTextCount === 0 ? 'pass' : 'info',
      message: smallTextCount === 0
        ? 'All text meets minimum 14px size'
        : `${smallTextCount} element(s) with font size <14px`,
      details: 'Small text can be hard to read on mobile'
    });

    // Check 6: Fixed positioning elements
    const fixedElements = document.querySelectorAll('[style*="position: fixed"], [class*="fixed"]');
    results.push({
      name: 'Fixed Position Elements',
      status: 'info',
      message: `${fixedElements.length} fixed position element(s)`,
      details: 'Fixed elements can cause layout issues on mobile'
    });

    // Check 7: Game grid container width using reliable data-testid selector
    const gameContainer = document.querySelector('[data-testid="game-grid-container"]');
    
    if (gameContainer) {
      const gridWidth = gameContainer.getBoundingClientRect().width;
      results.push({
        name: 'Game Grid Width',
        status: gridWidth <= 360 ? 'pass' : 'warn',
        message: `Game container width: ${gridWidth.toFixed(0)}px`,
        details: 'Target: ~350px max-width for consistency'
      });
    } else {
      results.push({
        name: 'Game Grid Width',
        status: 'info',
        message: 'Game grid not found on current page',
        details: 'Only applicable on /play route'
      });
    }

    // Check 8: Keyboard width using reliable data-testid selector
    // On mobile (<640px), keyboard should be full-width (intentional design)
    // On desktop (≥640px), keyboard should be ≤350px to match grid
    const keyboard = document.querySelector('[data-testid="keyboard-container"]');
    if (keyboard) {
      const keyboardWidth = keyboard.getBoundingClientRect().width;
      const isMobileKeyboard = viewportWidth < 640;
      const isFullWidthMobile = isMobileKeyboard && Math.abs(keyboardWidth - viewportWidth) <= 10;
      const isDesktopConstrained = !isMobileKeyboard && keyboardWidth <= 350;
      
      results.push({
        name: 'Keyboard Width',
        status: isFullWidthMobile || isDesktopConstrained ? 'pass' : 'warn',
        message: isMobileKeyboard 
          ? `Keyboard width: ${keyboardWidth.toFixed(0)}px (full-width mobile)`
          : `Keyboard width: ${keyboardWidth.toFixed(0)}px`,
        details: isMobileKeyboard 
          ? 'Full-width keyboard on mobile is intentional'
          : 'Should match game grid width (350px max on desktop)'
      });
    } else {
      results.push({
        name: 'Keyboard Width',
        status: 'info',
        message: 'Keyboard not found on current page',
        details: 'Only applicable on /play route'
      });
    }

    // Check 9: Responsive breakpoints
    const hasSmBreakpoint = window.matchMedia('(min-width: 640px)').matches;
    const hasMdBreakpoint = window.matchMedia('(min-width: 768px)').matches;
    const hasLgBreakpoint = window.matchMedia('(min-width: 1024px)').matches;

    results.push({
      name: 'Breakpoint Detection',
      status: 'info',
      message: `Active breakpoints: ${
        hasLgBreakpoint ? 'lg+' : hasMdBreakpoint ? 'md+' : hasSmBreakpoint ? 'sm+' : 'mobile'
      }`,
      details: 'Tailwind breakpoints: sm(640px), md(768px), lg(1024px)'
    });

    // Check 10: Landscape orientation
    const isLandscape = window.innerWidth > window.innerHeight;
    results.push({
      name: 'Orientation',
      status: 'info',
      message: isLandscape ? 'Landscape orientation' : 'Portrait orientation',
      details: `Screen: ${window.innerWidth}x${window.innerHeight}px`
    });

  } catch (error) {
    results.push({
      name: 'Mobile Layout Scan Error',
      status: 'fail',
      message: `Failed to run mobile layout scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Mobile Layout & Breakpoints Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`ℹ️  Info: ${summary.infoCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
